// 单一翻译资源来源：服务端与客户端共用，全部静态打包，保证同步可用（首屏即正确语言，无英文 key 闪烁）。
// 注意：本文件只 import JSON，不依赖任何 i18n 运行库，因此可同时被客户端（lib/i18n/index.ts）
// 与服务端（lib/i18n/server-metadata.ts）安全引用，避免把 i18next 等客户端库拉进服务端包。
import enTranslation from './locales/en/translation.json';
import zhTranslation from './locales/zh/translation.json';
import jaTranslation from './locales/ja/translation.json';
import enStudio from './locales/en/studio.json';
import zhStudio from './locales/zh/studio.json';
import esStudio from './locales/es/studio.json';
import frStudio from './locales/fr/studio.json';
import deStudio from './locales/de/studio.json';
import jaStudio from './locales/ja/studio.json';
import koStudio from './locales/ko/studio.json';
import ptStudio from './locales/pt/studio.json';

// 语言清单单一来源在 @flowmuse/api-core/locales（inngest 事件类型也依赖它），这里 re-export 保持既有 import 路径。
import { SUPPORTED_LOCALES, type Locale } from '@flowmuse/api-core/locales';
export { SUPPORTED_LOCALES, type Locale };
export const DEFAULT_LOCALE: Locale = 'en';

// market 命名空间随 Router 门户拆分迁入 router 子应用，主站不再打包。
export const NAMESPACES = ['translation', 'studio'] as const;
export const DEFAULT_NAMESPACE = 'translation';

// 受支持语言的展示元数据（单一来源）：`label` 为下拉中的母语名，`short` 为紧凑标签（如侧栏按钮）。
// 任何语言选择器都应从这里 + SUPPORTED_LOCALES 派生，避免各处维护重复的语言清单。
export const LOCALE_META: Record<Locale, { label: string; short: string }> = {
  en: { label: 'English', short: 'EN' },
  zh: { label: '中文', short: '中文' },
  es: { label: 'Español', short: 'ES' },
  fr: { label: 'Français', short: 'FR' },
  de: { label: 'Deutsch', short: 'DE' },
  ja: { label: '日本語', short: '日本語' },
  ko: { label: '한국어', short: '한국어' },
  pt: { label: 'Português', short: 'PT' },
};

// 画布（主应用）语言菜单。Studio 另支持 8 种语言；画布提供中文、英文、日文。
export const CANVAS_LOCALE_OPTIONS: { code: Locale; label: string }[] = [
  { code: 'zh', label: '中文' },
  { code: 'en', label: 'EN' },
  { code: 'ja', label: '日本語' },
];

// 注意：`translation` 全量译文为 en/zh/ja（画布）。es/fr/de/ko/pt 仅提供 `studio` 命名空间，
// 其余命名空间通过 i18next 的 fallbackLng='en' 自动回退到英文。
// Studio 组件使用 `studio` 命名空间（见 studio 内 useI18n('studio')），故这些语言下 Studio 完整本地化。
export const resources = {
  en: { translation: enTranslation, studio: enStudio },
  zh: { translation: zhTranslation, studio: zhStudio },
  es: { studio: esStudio },
  fr: { studio: frStudio },
  de: { studio: deStudio },
  ja: { translation: jaTranslation, studio: jaStudio },
  ko: { studio: koStudio },
  pt: { studio: ptStudio },
} as const;

// 把任意输入（cookie / Accept-Language / 'zh-CN' / 'pt-BR' 等）归一化为受支持的 locale。
// 与服务端 getLocale、客户端初始化保持完全一致，避免两端判定不同导致的二次切换。
export function normalizeLocale(value: string | null | undefined): Locale {
  const s = (value ?? '').trim().toLowerCase();
  if (s.startsWith('zh')) return 'zh';
  if (s.startsWith('es')) return 'es';
  if (s.startsWith('fr')) return 'fr';
  if (s.startsWith('de')) return 'de';
  if (s.startsWith('ja')) return 'ja';
  if (s.startsWith('ko')) return 'ko';
  if (s.startsWith('pt')) return 'pt';
  return 'en';
}
